import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import type { SiteCapability } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { CreateSiteSectionGroupDialog } from './CreateSiteSectionGroupDialog';
import { type UseAddSectionsToGroupProps, useAddSectionsToGroup } from './useAddSectionsToGroup';
import { useDeleteSiteItems } from './useDeleteSiteItems';

export interface SiteSectionGroupActionsButtonProps
    extends Pick<ButtonProps, 'size' | 'disabled'>,
        Omit<UseAddSectionsToGroupProps, 'createSiteSectionDialogTrigger'> {
    siteSectionGroup: api.SiteSectionGroup;
    sitePermissions: SiteCapability;
    site: api.Site;
    organization: api.Organization;
    siteStructure: api.SiteStructure;
}

/**
 * Action button to open actions palette for a section group.
 */
export function SiteSectionGroupActionsButton(props: SiteSectionGroupActionsButtonProps) {
    const {
        siteSectionGroup,
        sitePermissions,
        organization,
        site,
        siteStructure,
        upgrade,
        ...buttonProps
    } = props;

    const deleteSiteItems = useDeleteSiteItems({ organization, site });
    const createSiteSectionGroupDialogTrigger = useDialogTriggerState();
    const createSiteSectionDialogTrigger = useDialogTriggerState();
    const addSectionsToGroup = useAddSectionsToGroup({
        ...props,
        createSiteSectionDialogTrigger,
    });

    const featureNestedSectionGroup = useFlag('NESTED_SECTION_GROUPS');

    if (!sitePermissions.canAdminSite) {
        return null;
    }

    const palette = () => {
        return createPalette({
            groups: [
                {
                    key: 'add-sections',
                    commands: [
                        !siteSectionGroup.sectionGroup && featureNestedSectionGroup?.isEnabled
                            ? {
                                  key: 'add-new-group',
                                  title: 'New group',
                                  icon: upgrade ? Icon.Upgrade : Icon.AddSiteGroup,
                                  badge: getPaidIndicatorBadgeProps({ upgrade }),
                                  upgrade: Boolean(upgrade),
                                  trigger:
                                      upgrade?.onPress ??
                                      (() => createSiteSectionGroupDialogTrigger.open()),
                              }
                            : null,
                        {
                            key: 'add-sections',
                            title: 'Add sections…',
                            icon: Icon.AddSiteSection,
                            descendant: () => addSectionsToGroup(siteSectionGroup),
                        },
                    ].filter(filterOutNullable),
                },
                {
                    key: 'options',
                    commands: [
                        {
                            key: 'remove-group',
                            title: 'Remove',
                            icon: Icon.Trashcan,
                            trigger: () => deleteSiteItems([siteSectionGroup]),
                        },
                    ],
                },
            ],
        });
    };

    return (
        <>
            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog
                    site={site}
                    organization={organization}
                    siteSections={[]}
                    parent={siteSectionGroup.id}
                />
            </DialogRoot>
            <DialogRoot {...createSiteSectionDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    organizationId={organization.id}
                    target={{
                        type: 'siteSection',
                        siteStructure,
                        props: {
                            organization,
                            site,
                            inSiteSectionGroup: siteSectionGroup,
                        },
                    }}
                />
            </DialogRoot>
            <PaletteButton
                palette={palette}
                kind="nested"
                label="Actions"
                leadingIcon={Icon.MoreVertical}
                iconOnly
                {...buttonProps}
            />
        </>
    );
}
